/**
 * Cupones — réplica del backoffice de Skeleton.
 *   - 3 KPI cards: Cupones Activos / Usos Totales / Ahorro Generado
 *   - Botón "+ Nuevo Cupón"
 *   - Tabla con códigos, type, value, usos, fechas, status
 *   - Modal crear/editar
 */
import { createFileRoute } from '@tanstack/react-router'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { Card, Badge, Input, Label, Button, PageHeader } from '@/components/ui'
import { tenantApi } from '@/lib/api'
import { useAuth } from '@/lib/auth-store'
import { Plus, Tag, Edit, Trash2, ToggleLeft, ToggleRight, X } from 'lucide-react'

export const Route = createFileRoute('/store/$id/coupons')({
  component: StoreCouponsPage,
})

interface Coupon {
  id: string
  code: string
  type: 'percentage' | 'fixed_amount' | 'free_shipping'
  value: string
  minOrderAmount: number | null
  maxDiscount: number | null
  currency: string | null
  usageLimit: number | null
  usageCount: number
  startDate: string | null
  endDate: string | null
  isActive: boolean
  createdAt: string
}

interface CouponsResponse {
  data: Coupon[]
  stats: { activeCoupons: number; totalUsages: number; totalSavings: number }
}

function StoreCouponsPage() {
  const { activeTenantId } = useAuth()
  const [dialog, setDialog] = useState<{ mode: 'create' | 'edit'; coupon?: Coupon } | null>(null)

  const { data, isLoading } = useQuery({
    queryKey: ['coupons'],
    queryFn: () => tenantApi.get<CouponsResponse>('/api/v1/coupons'),
    enabled: !!activeTenantId,
  })

  const list = data?.data ?? []
  const stats = data?.stats ?? { activeCoupons: 0, totalUsages: 0, totalSavings: 0 }

  return (
    <>
      <PageHeader
        title="Cupones"
        description="Descuentos y códigos promocionales"
        action={
          <Button size="sm" onClick={() => setDialog({ mode: 'create' })}>
            <Plus className="size-4 mr-2" /> Nuevo Cupón
          </Button>
        }
      />

      <div className="p-8 space-y-4">
        <div className="space-y-3">
          <Card className="p-5">
            <p className="text-xs text-neutral-400 uppercase tracking-wider">Cupones Activos</p>
            <p className="text-3xl font-bold tabular-nums mt-1">{stats.activeCoupons}</p>
          </Card>
          <Card className="p-5">
            <p className="text-xs text-neutral-400 uppercase tracking-wider">Usos Totales</p>
            <p className="text-3xl font-bold tabular-nums mt-1">{stats.totalUsages}</p>
          </Card>
          <Card className="p-5">
            <p className="text-xs text-neutral-400 uppercase tracking-wider">Ahorro Generado</p>
            <p className="text-3xl font-bold tabular-nums mt-1">{formatCop(stats.totalSavings)}</p>
          </Card>
        </div>

        {isLoading && <p className="text-sm text-neutral-500">Cargando...</p>}
        {!isLoading && list.length === 0 && (
          <Card className="p-12 flex flex-col items-center justify-center text-center">
            <Tag className="size-12 text-neutral-700 mb-3" />
            <p className="text-base font-medium">Sin cupones</p>
            <p className="text-sm text-neutral-500 mt-1">Crea el primer cupón con el botón de arriba.</p>
          </Card>
        )}
        {list.length > 0 && (
          <Card>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-neutral-950 border-b border-neutral-800 text-xs text-neutral-500 uppercase tracking-wider">
                  <tr>
                    <th className="text-left px-4 py-3">Código</th>
                    <th className="text-left px-4 py-3">Tipo</th>
                    <th className="text-right px-4 py-3">Valor</th>
                    <th className="text-right px-4 py-3">Usos</th>
                    <th className="text-left px-4 py-3">Vigencia</th>
                    <th className="text-center px-4 py-3">Estado</th>
                    <th className="text-right px-4 py-3">Acciones</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-neutral-800">
                  {list.map((c) => (
                    <CouponRow key={c.id} coupon={c} onEdit={() => setDialog({ mode: 'edit', coupon: c })} />
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        )}
      </div>

      {dialog && <CouponDialog mode={dialog.mode} coupon={dialog.coupon} onClose={() => setDialog(null)} />}
    </>
  )
}

function CouponRow({ coupon, onEdit }: { coupon: Coupon; onEdit: () => void }) {
  const qc = useQueryClient()
  const remove = useMutation({
    mutationFn: () => tenantApi.del(`/api/v1/coupons/${coupon.id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['coupons'] }),
  })
  const toggle = useMutation({
    mutationFn: () => tenantApi.put(`/api/v1/coupons/${coupon.id}`, { isActive: !coupon.isActive }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['coupons'] }),
  })

  const typeLabels: Record<string, string> = {
    percentage: 'Porcentaje',
    fixed_amount: 'Monto fijo',
    free_shipping: 'Envío gratis',
  }
  const formatValue = () => {
    if (coupon.type === 'percentage') return `${parseFloat(coupon.value)}%`
    if (coupon.type === 'fixed_amount') return formatCop(parseFloat(coupon.value) * 100)
    return 'Gratis'
  }
  const vigencia = coupon.endDate
    ? `Hasta ${new Date(coupon.endDate).toLocaleDateString('es-CO')}`
    : 'Sin vencimiento'

  return (
    <tr className="hover:bg-neutral-900/50">
      <td className="px-4 py-3 font-mono font-bold uppercase">{coupon.code}</td>
      <td className="px-4 py-3 text-neutral-400">{typeLabels[coupon.type]}</td>
      <td className="px-4 py-3 text-right font-mono">{formatValue()}</td>
      <td className="px-4 py-3 text-right text-neutral-400 tabular-nums">
        {coupon.usageCount}{coupon.usageLimit ? ` / ${coupon.usageLimit}` : ''}
      </td>
      <td className="px-4 py-3 text-xs text-neutral-500">{vigencia}</td>
      <td className="px-4 py-3 text-center">
        <Badge variant={coupon.isActive ? 'success' : 'muted'}>{coupon.isActive ? 'Activo' : 'Inactivo'}</Badge>
      </td>
      <td className="px-4 py-3 text-right">
        <div className="flex items-center justify-end gap-1">
          <button onClick={() => toggle.mutate()} className="p-1.5 rounded text-neutral-400 hover:text-white hover:bg-neutral-800" title={coupon.isActive ? 'Desactivar' : 'Activar'}>
            {coupon.isActive ? <ToggleRight className="size-4" /> : <ToggleLeft className="size-4" />}
          </button>
          <button onClick={onEdit} className="p-1.5 rounded text-neutral-400 hover:text-white hover:bg-neutral-800" title="Editar">
            <Edit className="size-4" />
          </button>
          <button
            onClick={() => { if (confirm('¿Eliminar este cupón?')) remove.mutate() }}
            className="p-1.5 rounded text-red-400 hover:text-red-300 hover:bg-red-950/40"
            title="Eliminar"
          >
            <Trash2 className="size-4" />
          </button>
        </div>
      </td>
    </tr>
  )
}

function CouponDialog({ mode, coupon, onClose }: { mode: 'create' | 'edit'; coupon?: Coupon; onClose: () => void }) {
  const qc = useQueryClient()
  const [code, setCode] = useState(coupon?.code ?? '')
  const [type, setType] = useState<'percentage' | 'fixed_amount' | 'free_shipping'>(coupon?.type ?? 'percentage')
  const [value, setValue] = useState(coupon ? parseFloat(coupon.value) : 0)
  const [minOrderAmount, setMinOrderAmount] = useState<number | ''>(coupon?.minOrderAmount ?? '')
  const [maxDiscount, setMaxDiscount] = useState<number | ''>(coupon?.maxDiscount ?? '')
  const [usageLimit, setUsageLimit] = useState<number | ''>(coupon?.usageLimit ?? '')
  const [startDate, setStartDate] = useState(coupon?.startDate?.slice(0, 16) ?? '')
  const [endDate, setEndDate] = useState(coupon?.endDate?.slice(0, 16) ?? '')
  const [isActive, setIsActive] = useState(coupon?.isActive ?? true)

  const save = useMutation({
    mutationFn: (payload: any) =>
      mode === 'create'
        ? tenantApi.post('/api/v1/coupons', payload)
        : tenantApi.put(`/api/v1/coupons/${coupon!.id}`, payload),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['coupons'] })
      onClose()
    },
  })

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    save.mutate({
      code,
      type,
      value,
      minOrderAmount: minOrderAmount === '' ? null : Number(minOrderAmount),
      maxDiscount: maxDiscount === '' ? null : Number(maxDiscount),
      usageLimit: usageLimit === '' ? null : Number(usageLimit),
      startDate: startDate ? new Date(startDate).toISOString() : null,
      endDate: endDate ? new Date(endDate).toISOString() : null,
      isActive,
    })
  }

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4">
      <Card className="w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-5 border-b border-neutral-800">
          <h2 className="text-lg font-semibold">{mode === 'create' ? 'Crear Cupón' : 'Editar Cupón'}</h2>
          <button onClick={onClose} className="text-neutral-400 hover:text-white"><X className="size-5" /></button>
        </div>
        <form onSubmit={handleSubmit} className="p-5 space-y-4">
          <div>
            <Label>Código</Label>
            <Input value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} required placeholder="DESCUENTO10" autoFocus />
          </div>
          <div>
            <Label>Tipo</Label>
            <select
              value={type}
              onChange={(e) => setType(e.target.value as 'percentage' | 'fixed_amount' | 'free_shipping')}
              className="w-full h-9 px-3 bg-neutral-900 border border-neutral-700 rounded-md text-sm"
            >
              <option value="percentage">Porcentaje</option>
              <option value="fixed_amount">Monto fijo</option>
              <option value="free_shipping">Envío gratis</option>
            </select>
          </div>
          {type !== 'free_shipping' && (
            <div>
              <Label>{type === 'percentage' ? 'Porcentaje (%)' : 'Monto'}</Label>
              <Input type="number" min="0" value={value} onChange={(e) => setValue(parseFloat(e.target.value) || 0)} required />
            </div>
          )}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Monto mínimo</Label>
              <Input type="number" min="0" value={minOrderAmount} onChange={(e) => setMinOrderAmount(e.target.value === '' ? '' : Number(e.target.value))} />
            </div>
            <div>
              <Label>Descuento máx.</Label>
              <Input type="number" min="0" value={maxDiscount} onChange={(e) => setMaxDiscount(e.target.value === '' ? '' : Number(e.target.value))} />
            </div>
          </div>
          <div>
            <Label>Límite de uso</Label>
            <Input type="number" min="0" value={usageLimit} onChange={(e) => setUsageLimit(e.target.value === '' ? '' : Number(e.target.value))} placeholder="Sin límite" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Fecha inicio</Label>
              <Input type="datetime-local" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
            </div>
            <div>
              <Label>Fecha fin</Label>
              <Input type="datetime-local" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
            </div>
          </div>
          <div className="flex items-center gap-2">
            <input type="checkbox" id="isActive" checked={isActive} onChange={(e) => setIsActive(e.target.checked)} />
            <label htmlFor="isActive" className="text-sm">Activo</label>
          </div>
          {save.error && (
            <div className="p-3 rounded bg-red-900/20 border border-red-800 text-sm text-red-400">
              {(save.error as Error).message}
            </div>
          )}
          <div className="flex gap-2 justify-end pt-2 border-t border-neutral-800">
            <Button type="button" variant="outline" onClick={onClose}>Cancelar</Button>
            <Button type="submit" disabled={save.isPending}>{save.isPending ? 'Guardando...' : (mode === 'create' ? 'Crear' : 'Guardar')}</Button>
          </div>
        </form>
      </Card>
    </div>
  )
}

function formatCop(amount: number): string {
  return new Intl.NumberFormat('es-CO', { style: 'currency', currency: 'COP', minimumFractionDigits: 0, maximumFractionDigits: 0 })
    .format(amount / 100)
}
